<template>
    <div
        v-pendo-tooltip="{
            content: tooltipContent,
            multiline: true,
            arrow: true,
        }"
        class="voc-item-multiselect__tag"
        data-cy="voc-item-multiselect-tag"
        @mouseover="onMouseover">
        <product-area-icon
            v-if="color"
            :color="color" />
        <pendo-app-icon
            v-if="type === 'app'"
            :app="option" />
        <div
            ref="multiselectTagName"
            class="voc-item-multiselect__tag-name">
            <span
                v-if="category"
                class="voc-item-multiselect__tag-category">
                {{ category }}
            </span>
            {{ label }}
        </div>
        <pendo-icon
            v-if="isOpen"
            class="voc-item-multiselect__tag-x-icon"
            data-cy="voc-item-multiselect-remove-tag"
            type="x"
            size="14"
            @click.stop="removeItem(option)" />
    </div>
</template>

<script>
import { PendoIcon, PendoAppIcon, PendoTooltip } from '@pendo/components';
import ProductAreaIcon from '@/voc/components/icons/product-area/product-area.icon.vue';
import { elementHasOverflowX } from '@/utils/dom.utils';
import { NO_CATEGORY_STRING } from '@/voc/constants/constants';

export default {
    name: 'VocItemMultiselectTag',
    components: {
        PendoIcon,
        PendoAppIcon,
        ProductAreaIcon,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        option: {
            type: Object,
            required: true,
        },
        isOpen: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            required: true,
        },
        color: {
            type: String,
            default: null,
        },
    },
    emits: ['removeItem'],
    data () {
        return {
            tooltipContent: null,
        };
    },
    computed: {
        label () {
            switch (this.type) {
                case 'label':
                    return this.option.label;
                case 'product-area':
                    return this.option.name;
                case 'app':
                    return this.option.displayName;
                default:
                    return '';
            }
        },
        category () {
            if (this.type !== 'label') {
                return null;
            }

            return this.option.category === NO_CATEGORY_STRING ? null : `${this.option.category}:`;
        },
    },
    methods: {
        onMouseover () {
            const { multiselectTagName } = this.$refs;

            const { clientWidth, scrollWidth } = multiselectTagName;
            const hasOverflow = elementHasOverflowX({ clientWidth, scrollWidth });

            if (hasOverflow) {
                switch (this.type) {
                    case 'label':
                        this.tooltipContent =
                            this.option.category === NO_CATEGORY_STRING
                                ? this.option.label
                                : `${this.option.category}: ${this.option.label}`;
                        break;
                    case 'product-area':
                        this.tooltipContent = this.option.name;
                        break;
                    case 'app':
                        this.tooltipContent = this.option.displayName;
                        break;
                    default:
                        break;
                }
            } else {
                this.tooltipContent = null;
            }
        },
        removeItem (option) {
            this.$emit('removeItem', option);
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/voc/styles/voc.style.less';

.voc-item-multiselect {
    &.is-disabled {
        .voc-item-multiselect__tag {
            border: 1px solid var(--palette-gray-40);
        }
    }

    &__tag {
        display: flex;
        flex-direction: row;
        align-items: center;
        position: relative;
        grid-gap: 4px;
        height: 30px;
        color: var(--palette-gray-100);
        line-height: 20px;
        background-color: var(--palette-gray-30);
        padding: 7px;
        margin: 2px 2px 2px 0;
        border-radius: 3px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &[location='rule-builder'] {
            padding: 0 7px;
            margin: 0;
            height: 24px;
        }

        svg,
        .pendo-app-icon {
            margin-right: 4px;
            flex-shrink: 0;
        }
    }

    &__tag-name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__tag-category {
        font-weight: bold;
    }

    &__tag-x-icon {
        margin-left: 4px;
    }
}
</style>
